/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Companies API hooks (ISSUE-803 Phase 1C).
 *
 * 旧 use-distribution-api の置き換え先となる新エンドポイント
 * /api/companies / /api/company-groups / /api/delivery-profiles を扱う。
 */
import { restClient } from "lib/rest-client";
import { useCallback, useEffect, useRef, useState } from "react";

// =====================================
// Types
// =====================================

export type CompanyDomain = {
  id: string;
  domain: string;
  isPrimary: boolean;
};

export type CompanyContact = {
  id: string;
  email: string;
  displayName: string | null;
  optedOut: boolean;
  managedBy: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Company = {
  id: string;
  name: string;
  normalizedName: string;
  description: string | null;
  primaryDomain: string | null;
  domainCount: number;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CompanyDetail = {
  id: string;
  name: string;
  normalizedName: string;
  description: string | null;
  domains: CompanyDomain[];
  contacts: CompanyContact[];
  groupIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type CompanyListResponse = {
  items: Company[];
  total: number;
  page: number;
  limit: number;
};

export type CreateCompanyContactInput = {
  email: string;
  displayName?: string | null;
  optedOut?: boolean;
  notes?: string | null;
};

export type CreateCompanyInput = {
  name: string;
  description?: string | null;
  primaryDomain?: string | null;
  /** ISSUE-820: 同時登録する連絡先 (任意・複数) */
  contacts?: CreateCompanyContactInput[];
  /** ISSUE-821: 同時加入する企業グループ ID (任意・複数) */
  groupIds?: string[];
};

export type UpdateCompanyInput = {
  name?: string;
  description?: string | null;
};

export type AddDomainInput = {
  domain: string;
  isPrimary?: boolean;
};

export type AddContactInput = {
  email: string;
  displayName?: string | null;
  optedOut?: boolean;
  managedBy?: string | null;
  notes?: string | null;
};

export type UpdateContactInput = {
  displayName?: string | null;
  optedOut?: boolean;
  managedBy?: string | null;
  notes?: string | null;
};

export type ByDomainsItem = {
  domain: string;
  company: Company | null;
  groups: Array<{ id: string; name: string; color: string | null }>;
};

export type ByDomainsResponse = {
  items: ByDomainsItem[];
};

export type MergeResponse = {
  masterId: string;
  absorbedId: string;
  domainsMoved: number;
  contactsMoved: number;
  contactsSoftDeleted: number;
  membershipsMoved: number;
};

// =====================================
// CSV Import / Export (ISSUE-823)
// =====================================

export type CsvImportRowStatus =
  | "NEW"
  | "LINK_DOMAIN"
  | "MERGE_CANDIDATE"
  | "COMPANY_ONLY_NEW"
  | "COMPANY_ONLY_LINK"
  | "SKIP_DUPLICATE_EMAIL"
  | "ERROR";

export type CsvImportRowDecision = "ACCEPT" | "CREATE_NEW" | "SKIP";

export type CsvImportRowInput = {
  rowNumber: number;
  companyName: string;
  primaryDomain: string | null;
  email: string | null;
  contactName: string | null;
  optedOut: boolean;
  notes: string | null;
};

export type CsvImportPreviewRow = {
  rowNumber: number;
  input: CsvImportRowInput;
  status: CsvImportRowStatus;
  existingCompanyId: string | null;
  existingCompanyName: string | null;
  errorMessage: string | null;
};

export type CsvImportPreviewSummary = {
  total: number;
  new: number;
  linkDomain: number;
  mergeCandidate: number;
  companyOnlyNew: number;
  companyOnlyLink: number;
  skipDuplicateEmail: number;
  error: number;
};

export type CsvImportPreviewResponse = {
  rows: CsvImportPreviewRow[];
  summary: CsvImportPreviewSummary;
};

export type CsvImportExecuteRow = {
  rowNumber: number;
  input: CsvImportRowInput;
  status: CsvImportRowStatus;
  existingCompanyId: string | null;
  decision: CsvImportRowDecision;
};

export type CsvImportExecuteSummary = {
  total: number;
  createdCompanies: number;
  linkedToExisting: number;
  createdContacts: number;
  skipped: number;
  errors: number;
};

export type CsvImportErrorDetail = {
  rowNumber: number;
  message: string;
};

export type CsvImportExecuteResponse = {
  summary: CsvImportExecuteSummary;
  errors: CsvImportErrorDetail[];
};

// =====================================
// fetch helper
// =====================================
// List companies
// =====================================

export type UseCompaniesApiOptions = {
  page?: number;
  limit?: number;
  search?: string;
  /** skip フェッチ (詳細画面など、別 hook で取得する場合) */
  skip?: boolean;
};

export type UseCompaniesApiResult = {
  data: CompanyListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<CompanyListResponse>;
};

/**
 * 企業マスタ一覧を取得する hook。
 * @param {UseCompaniesApiOptions} options ページング・検索条件
 * @return {UseCompaniesApiResult} data, error, loading, refetch
 */
export function useCompaniesApi(
  options: UseCompaniesApiOptions = {},
): UseCompaniesApiResult {
  const [data, setData] = useState<CompanyListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchList = useCallback(async (): Promise<CompanyListResponse> => {
    setLoading(true);
    setError(undefined);
    try {
      const params: Record<string, string> = {};
      if (options.page) params.page = String(options.page);
      if (options.limit) params.limit = String(options.limit);
      if (options.search) params.search = options.search;
      const res = await restClient.get<CompanyListResponse>(
        "/companies",
        params,
      );
      setData(res);
      return res;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.page, options.limit, options.search]);

  useEffect(() => {
    if (!options.skip) {
      fetchList().catch(() => undefined);
    }
  }, [options.skip, fetchList]);

  return { data, error, loading, refetch: fetchList };
}

// =====================================
// Get company detail
// =====================================

export type UseCompanyApiOptions = {
  id: string;
  skip?: boolean;
};

export type UseCompanyApiResult = {
  data: CompanyDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<CompanyDetail>;
};

/**
 * 企業詳細を取得する hook。
 * @param {UseCompanyApiOptions} options 企業 ID とスキップフラグ
 * @return {UseCompanyApiResult} data, error, loading, refetch
 */
export function useCompanyApi(
  options: UseCompanyApiOptions,
): UseCompanyApiResult {
  const [data, setData] = useState<CompanyDetail | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchOne = useCallback(async (): Promise<CompanyDetail> => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await restClient.get<CompanyDetail>(
        `/companies/${options.id}`,
      );
      setData(res);
      return res;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchOne().catch(() => undefined);
    }
  }, [options.skip, options.id, fetchOne]);

  return { data, error, loading, refetch: fetchOne };
}

// =====================================
// Mutations (create / update / delete / domains / contacts / merge)
// =====================================

export type UseCompanyMutationsResult = {
  createCompany: (input: CreateCompanyInput) => Promise<Company>;
  updateCompany: (id: string, input: UpdateCompanyInput) => Promise<Company>;
  deleteCompany: (id: string) => Promise<void>;
  addDomain: (
    companyId: string,
    input: AddDomainInput,
  ) => Promise<CompanyDomain>;
  removeDomain: (companyId: string, domain: string) => Promise<void>;
  addContact: (
    companyId: string,
    input: AddContactInput,
  ) => Promise<CompanyContact>;
  updateContact: (
    companyId: string,
    contactId: string,
    input: UpdateContactInput,
  ) => Promise<CompanyContact>;
  deleteContact: (companyId: string, contactId: string) => Promise<void>;
  mergeCompany: (
    masterId: string,
    absorbedId: string,
  ) => Promise<MergeResponse>;
  byDomains: (domains: string[]) => Promise<ByDomainsResponse>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * 企業マスタ書き込み系の mutation hooks をまとめて返す。
 * @return {UseCompanyMutationsResult} mutations と loading, error
 */
export function useCompanyMutations(): UseCompanyMutationsResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const wrap = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(undefined);
    try {
      return await fn();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCompany = useCallback(
    (input: CreateCompanyInput) =>
      wrap(() => restClient.post<Company>("/companies", input)),
    [wrap],
  );

  const updateCompany = useCallback(
    (id: string, input: UpdateCompanyInput) =>
      wrap(() => restClient.patch<Company>(`/companies/${id}`, input)),
    [wrap],
  );

  const deleteCompany = useCallback(
    (id: string) => wrap(() => restClient.delete<void>(`/companies/${id}`)),
    [wrap],
  );

  const addDomain = useCallback(
    (companyId: string, input: AddDomainInput) =>
      wrap(() =>
        restClient.post<CompanyDomain>(
          `/companies/${companyId}/domains`,
          input,
        ),
      ),
    [wrap],
  );

  const removeDomain = useCallback(
    (companyId: string, domain: string) =>
      wrap(() =>
        restClient.delete<void>(
          `/companies/${companyId}/domains/${encodeURIComponent(domain)}`,
        ),
      ),
    [wrap],
  );

  const addContact = useCallback(
    (companyId: string, input: AddContactInput) =>
      wrap(() =>
        restClient.post<CompanyContact>(
          `/companies/${companyId}/contacts`,
          input,
        ),
      ),
    [wrap],
  );

  const updateContact = useCallback(
    (companyId: string, contactId: string, input: UpdateContactInput) =>
      wrap(() =>
        restClient.patch<CompanyContact>(
          `/companies/${companyId}/contacts/${contactId}`,
          input,
        ),
      ),
    [wrap],
  );

  const deleteContact = useCallback(
    (companyId: string, contactId: string) =>
      wrap(() =>
        restClient.delete<void>(
          `/companies/${companyId}/contacts/${contactId}`,
        ),
      ),
    [wrap],
  );

  const mergeCompany = useCallback(
    (masterId: string, absorbedId: string) =>
      wrap(() =>
        restClient.post<MergeResponse>(`/companies/${masterId}/merge`, {
          absorbedId,
        }),
      ),
    [wrap],
  );

  const byDomains = useCallback(
    (domains: string[]) =>
      wrap(() =>
        restClient.post<ByDomainsResponse>("/companies/by-domains", {
          domains,
        }),
      ),
    [wrap],
  );

  return {
    createCompany,
    updateCompany,
    deleteCompany,
    addDomain,
    removeDomain,
    addContact,
    updateContact,
    deleteContact,
    mergeCompany,
    byDomains,
    loading,
    error,
  };
}

// =====================================
// CSV Import / Export hooks (ISSUE-823)
// =====================================

export type UseCompanyCsvResult = {
  previewImport: (file: File) => Promise<CsvImportPreviewResponse>;
  executeImport: (
    rows: CsvImportExecuteRow[],
  ) => Promise<CsvImportExecuteResponse>;
  exportCsv: (params?: { search?: string }) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
};

const BLOB_REVOKE_DELAY_MS = 200;

/**
 * 企業マスタの CSV インポート・エクスポート hooks。
 * @return {UseCompanyCsvResult} previewImport, executeImport, exportCsv と loading/error
 */
export function useCompanyCsv(): UseCompanyCsvResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const exportTimersRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const timers = exportTimersRef.current;
    return () => {
      for (const t of timers) window.clearTimeout(t);
      timers.clear();
    };
  }, []);

  const wrap = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(undefined);
    try {
      return await fn();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const previewImport = useCallback(
    (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return wrap(() =>
        restClient.postFormData<CsvImportPreviewResponse>(
          "/companies/import-csv/preview",
          formData,
        ),
      );
    },
    [wrap],
  );

  const executeImport = useCallback(
    (rows: CsvImportExecuteRow[]) =>
      wrap(() =>
        restClient.post<CsvImportExecuteResponse>(
          "/companies/import-csv/execute",
          { rows },
        ),
      ),
    [wrap],
  );

  const exportCsv = useCallback(
    (params?: { search?: string }) =>
      wrap(async () => {
        const query: Record<string, string> = {};
        if (params?.search) query.search = params.search;
        const { blob, filename } = await restClient.getBlob(
          "/companies/export-csv",
          Object.keys(query).length ? query : undefined,
        );
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename || "strix_companies.csv";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        // 即時 revoke するとブラウザがダウンロード開始前に blob を解放し、
        // navigate 扱いで blob URL を新規タブで開いてしまうため遅延が必要。
        const timer = window.setTimeout(() => {
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(url);
          exportTimersRef.current.delete(timer);
        }, BLOB_REVOKE_DELAY_MS);
        exportTimersRef.current.add(timer);
      }),
    [wrap],
  );

  return { previewImport, executeImport, exportCsv, loading, error };
}
